import type { AppEnv } from "@contexts/AppContext";

/** PostHog Cloud EU ingest endpoint, used when a key is set without a host. */
export const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";

export type StaticProps = Record<string, string | number | boolean>;

export interface AnalyticsConfig {
  host: string;
  key: string;
  /** Deployment labels (hub, cell, env, ...) attached to every event. */
  properties: StaticProps;
}

/**
 * Parse the NA_POSTHOG_PROPERTIES JSON string. Only flat scalar values are
 * kept; anything malformed yields an empty map rather than breaking startup.
 */
export function parseStaticProps(raw?: unknown): StaticProps {
  // Helm values may land here as an already-parsed object instead of a string
  if (raw && typeof raw === "object") return parseStaticProps(JSON.stringify(raw));
  if (typeof raw !== "string" || !raw.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) =>
        ["string", "number", "boolean"].includes(typeof v),
      ),
    ) as StaticProps;
  } catch {
    return {};
  }
}

/**
 * Resolve analytics config from runtime env. Returns null (analytics inert)
 * when no project key is configured.
 */
export function resolveAnalyticsConfig(
  env: Pick<AppEnv, "NA_POSTHOG_HOST" | "NA_POSTHOG_KEY" | "NA_POSTHOG_PROPERTIES">,
): AnalyticsConfig | null {
  const key = env.NA_POSTHOG_KEY?.trim();
  if (!key) return null;
  return {
    key,
    host: env.NA_POSTHOG_HOST?.trim() || DEFAULT_POSTHOG_HOST,
    properties: parseStaticProps(env.NA_POSTHOG_PROPERTIES),
  };
}
